import { useState } from 'react';
import styled from 'styled-components';

import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { DropdownSelect } from '@sorare/core/src/atoms/inputs/DropdownSelect';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyL,
  BodyS,
  HeadlineL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { groupBy } from '@sorare/core/src/lib/arrays';
import { mobileAndAbove } from '@sorare/core/src/style/mediaQuery';

import type { Department, Job } from '#src/Careers/types.ts';
import { Section, StyledContainer, Title } from '#src/Careers/ui.tsx';

enum EmploymentType {
  FullTime = 'Permanent',
  PartTime = 'PartTime',
  Intern = 'Internship',
  Contract = 'Freelance',
  Temporary = 'Apprenticeship',
}

const BlackSection = styled(Section)`
  padding: var(--triple-unit) 0 var(--quadruple-unit);
`;
const Form = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--double-unit);
  @media ${mobileAndAbove} {
    flex-direction: row;
  }
  & > * {
    flex: 1;
  }
`;

const DepartmentsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: var(--triple-unit);
  @media ${mobileAndAbove} {
    flex-direction: row;
  }
`;

const DepartmentTitle = styled(Title)`
  margin-left: var(--unit);
  margin-bottom: var(--double-unit);
  color: var(--c-blue-400);
`;
const JobContainer = styled(ButtonBase)`
  width: 100%;
  height: auto;
  display: block;
  text-align: left;
  padding: var(--unit) var(--double-unit);
  border-radius: 2em;

  &:hover {
    background-color: rgba(var(--c-rgb-blue-400), 0.1);
  }
  &:not(:last-of-type) {
    margin-bottom: var(--double-unit);
  }
`;

type Props = {
  jobs: Job[];
};

export const JobBoard = ({ jobs }: Props) => {
  const isLaptop = useIsLaptop();

  const [department, setDepartment] = useState<string>('');
  const [office, setOffice] = useState<string>('');
  const [jobType, setJobType] = useState<string>('');

  const filteredJobs = jobs.filter(
    job =>
      (!office || job.location === office) &&
      (!department || job.department === department) &&
      (!jobType || job.employmentType === jobType)
  );

  const officesOptions = [
    { label: 'All offices', value: '' },
    ...Array.from(new Set(jobs.map(job => job.location))).map(off => ({
      label: off,
      value: off,
    })),
  ];

  const departmentsOptions = [
    { label: 'All departments', value: '' },
    ...Array.from(new Set(jobs.map(job => job.department))).map(off => ({
      label: off,
      value: off,
    })),
  ];

  const jobTypeOptions = [
    { label: 'All job types', value: '' },
    ...Array.from(new Set(jobs.map(job => job.employmentType))).map(off => ({
      label: off,
      value: off,
    })),
  ];

  const columns: Department[][] = [[], [], []];
  const oneThirdTotalJobs = Math.floor(filteredJobs.length / columns.length);
  Object.entries(groupBy(filteredJobs, job => job.department)).forEach(
    ([dept, deptJobs = []]) => {
      for (let i = 0; i < columns.length; i += 1) {
        const depts: Department[] = columns[i];
        const sizeColumn = depts.reduce((acc, d) => acc + d.jobs.length, 0);
        if (
          sizeColumn + deptJobs.length <= oneThirdTotalJobs ||
          sizeColumn === 0
        ) {
          columns[i].push({ name: dept, jobs: deptJobs });
          break;
        } else if (i === columns.length - 1) {
          columns[0].push({ name: dept, jobs: deptJobs });
        }
      }
    }
  );

  if (jobs.length === 0) {
    return (
      <BlackSection center={!isLaptop}>
        <StyledContainer>
          <Title>Sorare Careers</Title>
          <HeadlineL as="h3">No vacancies</HeadlineL>
        </StyledContainer>
      </BlackSection>
    );
  }

  return (
    <>
      <BlackSection center={!isLaptop}>
        <StyledContainer>
          <Title>Sorare Careers</Title>
          <Form>
            <DropdownSelect
              size="medium"
              value={department}
              onChange={value => setDepartment(value)}
              options={departmentsOptions}
            />
            <DropdownSelect
              size="medium"
              value={office}
              onChange={value => setOffice(value)}
              options={officesOptions}
            />
            <DropdownSelect
              size="medium"
              value={jobType}
              onChange={value => setJobType(value)}
              options={jobTypeOptions}
            />
          </Form>
        </StyledContainer>
      </BlackSection>
      {!!filteredJobs.length && (
        <Section $spacing>
          <StyledContainer>
            <DepartmentsContainer>
              {columns.map((departments, i) => (
                // oxlint-disable-next-line react/no-array-index-key
                <Vertical key={`column-${i}`} gap={3}>
                  {departments.map(dept => (
                    <div key={dept.name}>
                      <DepartmentTitle>{dept.name}</DepartmentTitle>
                      {dept.jobs.map(job => (
                        <JobContainer key={job.id} to={`?jid=${job.id}`}>
                          <BodyL color="var(--c-nd-800)">{job.title}</BodyL>
                          <Horizontal>
                            <BodyS>
                              {
                                EmploymentType[
                                  job.employmentType as keyof typeof EmploymentType
                                ]
                              }
                            </BodyS>
                            <LabelM color="var(--c-nd-600)">
                              {job.location}
                            </LabelM>
                          </Horizontal>
                        </JobContainer>
                      ))}
                    </div>
                  ))}
                </Vertical>
              ))}
            </DepartmentsContainer>
          </StyledContainer>
        </Section>
      )}
    </>
  );
};
